import * as THREE from 'three';
import {
  buildBlob,
  buildBulb,
  buildPetalCup,
  buildBell,
  buildSpire,
  buildStem,
  buildFrond,
  buildBlade,
  buildMat,
  buildPetalRing
} from './builders';
import type { FloraArchetype, FloraSpec } from '../BiomeProfile';

/**
 * Each archetype builder takes a normalised scale `s` (1.0 = nominal) plus
 * pre-baked materials and returns a `THREE.Object3D` rooted at the ground
 * plane (y=0). The Flora factory handles placement, rotation and tinting.
 *
 * All archetypes intentionally avoid raw boxy geometry. Stems are bent,
 * canopies are noise-displaced spheres, petals are lathe revolutions.
 */

interface BuildContext {
  readonly bodyMat: THREE.MeshStandardMaterial;
  readonly accentMat: THREE.MeshStandardMaterial;
  readonly emissiveMat: THREE.MeshStandardMaterial | null;
}

export function buildArchetype(
  archetype: FloraArchetype,
  scale: number,
  ctx: BuildContext
): THREE.Object3D {
  switch (archetype) {
    case 'salt-bloom':
      return buildSaltBloom(scale, ctx);
    case 'glass-cactus':
      return buildGlassCactus(scale, ctx);
    case 'mineral-mat':
      return buildMineralMat(scale, ctx);
    case 'vesicle-tree':
      return buildVesicleTree(scale, ctx);
    case 'fleshy-frond':
      return buildFleshyFrond(scale, ctx);
    case 'nectar-bell':
      return buildNectarBell(scale, ctx);
    case 'pulsing-root':
      return buildPulsingRoot(scale, ctx);
    case 'ferrous-spire':
      return buildFerrousSpire(scale, ctx);
    case 'rust-shrub':
      return buildRustShrub(scale, ctx);
    case 'amber-blossom':
      return buildAmberBlossom(scale, ctx);
    case 'glass-blade-fan':
      return buildGlassBladeFan(scale, ctx);
    case 'metal-flower':
      return buildMetalFlower(scale, ctx);
    case 'heat-bulb':
      return buildHeatBulb(scale, ctx);
    case 'ice-spire':
      return buildIceSpire(scale, ctx);
    case 'crystal-petal':
      return buildCrystalPetal(scale, ctx);
    case 'frost-moss':
      return buildFrostMoss(scale, ctx);
    case 'storm-flower':
      return buildStormFlower(scale, ctx);
    case 'ice-frond':
      return buildIceFrond(scale, ctx);
    case 'fibrous-ice-shrub':
      return buildFibrousIceShrub(scale, ctx);
    case 'metal-crust':
      return buildMetalCrust(scale, ctx);
    case 'photophore-bush':
      return buildPhotophoreBush(scale, ctx);
    case 'lumen-mat':
      return buildLumenMat(scale, ctx);
    case 'glow-stalk':
      return buildGlowStalk(scale, ctx);
    case 'crack-fungus':
      return buildCrackFungus(scale, ctx);
    case 'shadow-mineral':
      return buildShadowMineral(scale, ctx);
  }
}

/** Estimate of the radius (XZ) of an archetype at unit scale — used for
 *  collision/obstacle sizing without round-tripping through bounding-box
 *  computation on every instance. */
export function estimatedRadius(spec: FloraSpec, scale: number): number {
  switch (spec.archetype) {
    case 'vesicle-tree':
    case 'ferrous-spire':
    case 'ice-spire':
      return 0.8 * scale;
    case 'glass-cactus':
    case 'rust-shrub':
    case 'photophore-bush':
    case 'amber-blossom':
    case 'heat-bulb':
    case 'fibrous-ice-shrub':
      return 0.95 * scale;
    case 'glass-blade-fan':
    case 'fleshy-frond':
    case 'ice-frond':
      return 0.55 * scale;
    case 'salt-bloom':
    case 'nectar-bell':
    case 'metal-flower':
    case 'crystal-petal':
    case 'storm-flower':
    case 'glow-stalk':
      return 0.6 * scale;
    case 'pulsing-root':
      return 0.85 * scale;
    case 'mineral-mat':
    case 'frost-moss':
    case 'metal-crust':
    case 'crack-fungus':
    case 'lumen-mat':
    case 'shadow-mineral':
      return 1.4 * scale;
  }
  return 0.8 * scale;
}

/* ───────────────────── archetype implementations ───────────────────── */

function buildSaltBloom(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const stem = buildStem(0.75 * s, 0.06 * s, 0.04 * s, ctx.bodyMat, 0.15);
  group.add(stem);

  const cup = buildPetalCup(0.45 * s, 0.42 * s, ctx.accentMat);
  cup.position.y = 0.7 * s;
  group.add(cup);

  // Emissive crystal cluster at the cup center.
  if (ctx.emissiveMat) {
    const crystal = buildBlob(0.18 * s, ctx.emissiveMat, 1, 0.4);
    crystal.position.y = 0.95 * s;
    crystal.scale.y = 1.6;
    group.add(crystal);
  }
  return group;
}

function buildGlassCactus(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  // Stack of bulbs giving an organic onion-tower silhouette.
  let y = 0;
  let r = 0.42 * s;
  for (let i = 0; i < 3; i++) {
    const bulb = buildBulb(r, i % 2 === 0 ? ctx.bodyMat : ctx.accentMat);
    bulb.position.y = y;
    bulb.rotation.y = Math.random() * Math.PI;
    group.add(bulb);
    y += r * 1.05;
    r *= 0.72;
  }
  return group;
}

function buildMineralMat(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const mat = buildMat(1.2 * s, ctx.bodyMat);
  group.add(mat);
  // A scattering of small petal-curls to break the disc silhouette.
  const petals = 4 + Math.floor(Math.random() * 4);
  for (let i = 0; i < petals; i++) {
    const petal = buildFrond(0.45 * s, 0.18 * s, ctx.accentMat, 0.7);
    petal.rotation.set(0.4, Math.random() * Math.PI * 2, 0);
    petal.position.set(
      (Math.random() - 0.5) * 1.4 * s,
      0.04 * s,
      (Math.random() - 0.5) * 1.4 * s
    );
    petal.scale.setScalar(0.7 + Math.random() * 0.4);
    group.add(petal);
  }
  return group;
}

function buildVesicleTree(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const trunkHeight = 2.6 * s;
  const trunk = buildStem(trunkHeight, 0.18 * s, 0.07 * s, ctx.bodyMat, 0.22);
  group.add(trunk);

  // A soft cluster of bulb canopies near the top — multiple offsets so
  // the silhouette breaks like real foliage.
  const canopyCount = 3 + Math.floor(Math.random() * 3);
  for (let i = 0; i < canopyCount; i++) {
    const r = (0.55 + Math.random() * 0.2) * s;
    const blob = buildBlob(r, ctx.accentMat, 2, 0.18);
    blob.position.set(
      (Math.random() - 0.5) * 0.5 * s,
      trunkHeight + (Math.random() - 0.2) * r,
      (Math.random() - 0.5) * 0.5 * s
    );
    group.add(blob);
  }

  // Optional luminescent vesicles (mounted onto the canopy area).
  if (ctx.emissiveMat) {
    const dotCount = 4 + Math.floor(Math.random() * 4);
    for (let i = 0; i < dotCount; i++) {
      const r = (0.07 + Math.random() * 0.06) * s;
      const dot = buildBlob(r, ctx.emissiveMat, 1, 0.3);
      const angle = Math.random() * Math.PI * 2;
      const radius = 0.55 * s + Math.random() * 0.2 * s;
      dot.position.set(
        Math.cos(angle) * radius,
        trunkHeight + Math.random() * 0.6 * s,
        Math.sin(angle) * radius
      );
      group.add(dot);
    }
  }
  return group;
}

function buildFleshyFrond(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const frondCount = 4 + Math.floor(Math.random() * 3);
  for (let i = 0; i < frondCount; i++) {
    const length = (0.85 + Math.random() * 0.4) * s;
    const width = (0.22 + Math.random() * 0.1) * s;
    const frond = buildFrond(
      length,
      width,
      i % 2 === 0 ? ctx.bodyMat : ctx.accentMat,
      0.55
    );
    frond.rotation.set(
      0.18 + Math.random() * 0.2,
      (i / frondCount) * Math.PI * 2 + Math.random() * 0.4,
      (Math.random() - 0.5) * 0.4
    );
    frond.scale.x = 0.8 + Math.random() * 0.4;
    group.add(frond);
  }
  return group;
}

function buildNectarBell(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const stem = buildStem(0.95 * s, 0.05 * s, 0.03 * s, ctx.bodyMat, 0.32);
  group.add(stem);

  // Bell-shaped flower head, hanging slightly downward.
  const bell = buildBell(0.3 * s, 0.28 * s, ctx.accentMat);
  bell.position.y = 0.95 * s;
  bell.rotation.x = Math.PI; // turn the bell mouth downward
  group.add(bell);

  if (ctx.emissiveMat) {
    const core = buildBlob(0.08 * s, ctx.emissiveMat, 1, 0.3);
    core.position.y = 0.78 * s;
    group.add(core);
  }
  return group;
}

function buildFerrousSpire(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const height = 2.4 * s;
  const spire = buildSpire(height, 0.32 * s, ctx.bodyMat);
  group.add(spire);

  // A few smaller flanking spires.
  const sub = 2 + Math.floor(Math.random() * 2);
  for (let i = 0; i < sub; i++) {
    const ss = (0.45 + Math.random() * 0.25) * s;
    const small = buildSpire(2.2 * ss, 0.3 * ss, ctx.accentMat);
    const angle = Math.random() * Math.PI * 2;
    small.position.set(
      Math.cos(angle) * 0.55 * s,
      0,
      Math.sin(angle) * 0.55 * s
    );
    small.rotation.y = Math.random() * Math.PI;
    group.add(small);
  }
  return group;
}

function buildRustShrub(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  // A rounded blob body with a few accent petal frills.
  const body = buildBlob(0.55 * s, ctx.bodyMat, 2, 0.25);
  body.position.y = 0.4 * s;
  body.scale.y = 0.78;
  group.add(body);

  const petals = 5 + Math.floor(Math.random() * 4);
  for (let i = 0; i < petals; i++) {
    const petal = buildFrond(0.42 * s, 0.16 * s, ctx.accentMat, 0.8);
    petal.position.y = 0.55 * s;
    petal.rotation.set(
      0.4 + Math.random() * 0.2,
      (i / petals) * Math.PI * 2 + Math.random() * 0.3,
      0
    );
    group.add(petal);
  }
  return group;
}

function buildAmberBlossom(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const stem = buildStem(0.55 * s, 0.05 * s, 0.03 * s, ctx.bodyMat, 0.18);
  group.add(stem);

  const ring = buildPetalRing(8, 0.4 * s, 0.18 * s, ctx.accentMat, 0.55);
  ring.position.y = 0.55 * s;
  group.add(ring);

  if (ctx.emissiveMat) {
    const core = buildBlob(0.15 * s, ctx.emissiveMat, 2, 0.4);
    core.position.y = 0.6 * s;
    group.add(core);
  }
  return group;
}

function buildGlassBladeFan(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const blades = 5 + Math.floor(Math.random() * 4);
  for (let i = 0; i < blades; i++) {
    const length = (1.3 + Math.random() * 0.6) * s;
    const width = (0.18 + Math.random() * 0.08) * s;
    const blade = buildBlade(
      length,
      width,
      i % 2 === 0 ? ctx.bodyMat : ctx.accentMat
    );
    blade.rotation.set(
      0.05 + Math.random() * 0.15,
      (i / blades) * Math.PI * 2 + Math.random() * 0.3,
      (Math.random() - 0.5) * 0.18
    );
    blade.scale.x = 0.7 + Math.random() * 0.5;
    group.add(blade);
  }
  return group;
}

function buildMetalFlower(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const stem = buildStem(0.75 * s, 0.06 * s, 0.04 * s, ctx.bodyMat, 0.18);
  group.add(stem);

  // Outer petal ring.
  const outer = buildPetalRing(7, 0.48 * s, 0.22 * s, ctx.accentMat, 0.45);
  outer.position.y = 0.78 * s;
  group.add(outer);

  // Inner petal ring (offset rotation, smaller).
  const inner = buildPetalRing(5, 0.32 * s, 0.16 * s, ctx.bodyMat, 0.25);
  inner.position.y = 0.83 * s;
  inner.rotation.y = Math.PI / 5;
  group.add(inner);

  if (ctx.emissiveMat) {
    const core = buildBlob(0.18 * s, ctx.emissiveMat, 2, 0.45);
    core.position.y = 0.84 * s;
    group.add(core);
  }
  return group;
}

function buildHeatBulb(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const cluster = 3 + Math.floor(Math.random() * 3);
  for (let i = 0; i < cluster; i++) {
    const r = (0.32 + Math.random() * 0.15) * s;
    const bulb = buildBulb(r, ctx.bodyMat);
    const angle = (i / cluster) * Math.PI * 2;
    bulb.position.set(
      Math.cos(angle) * 0.25 * s,
      0,
      Math.sin(angle) * 0.25 * s
    );
    bulb.rotation.y = Math.random() * Math.PI;
    group.add(bulb);
    if (ctx.emissiveMat) {
      const core = buildBlob(r * 0.4, ctx.emissiveMat, 1, 0.35);
      core.position.copy(bulb.position);
      core.position.y += r * 0.55;
      group.add(core);
    }
  }
  return group;
}

function buildIceSpire(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const heights: number[] = [];
  const cluster = 3 + Math.floor(Math.random() * 3);
  for (let i = 0; i < cluster; i++) {
    const h = (1.5 + Math.random() * 1.2) * s;
    heights.push(h);
    const r = (0.18 + Math.random() * 0.08) * s;
    const spire = buildSpire(h, r, i % 2 === 0 ? ctx.bodyMat : ctx.accentMat);
    const angle = (i / cluster) * Math.PI * 2;
    const dist = 0.18 * s + Math.random() * 0.18 * s;
    spire.position.set(Math.cos(angle) * dist, 0, Math.sin(angle) * dist);
    spire.rotation.y = Math.random() * Math.PI;
    spire.rotation.z = (Math.random() - 0.5) * 0.18;
    group.add(spire);
  }
  // Frozen petal flourish at the base — read as floral, not jagged.
  if (ctx.emissiveMat) {
    const ring = buildPetalRing(6, 0.45 * s, 0.16 * s, ctx.emissiveMat, 0.7);
    ring.position.y = 0.05 * s;
    ring.scale.setScalar(0.85);
    group.add(ring);
  }
  return group;
}

function buildCrystalPetal(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const stem = buildStem(0.55 * s, 0.04 * s, 0.025 * s, ctx.bodyMat, 0.15);
  group.add(stem);
  // Crystalline lathe head — kept curvy, not faceted, by using a smooth
  // bell profile.
  const petal = buildPetalCup(0.5 * s, 0.4 * s, ctx.accentMat);
  petal.position.y = 0.55 * s;
  group.add(petal);
  if (ctx.emissiveMat) {
    const core = buildBlob(0.16 * s, ctx.emissiveMat, 2, 0.5);
    core.position.y = 0.78 * s;
    core.scale.y = 1.4;
    group.add(core);
  }
  return group;
}

function buildFrostMoss(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const mat = buildMat(1.4 * s, ctx.bodyMat);
  group.add(mat);
  // Tufts of micro-blobs to give it luminescent volume.
  const tufts = 6 + Math.floor(Math.random() * 5);
  for (let i = 0; i < tufts; i++) {
    const r = (0.12 + Math.random() * 0.1) * s;
    const tuft = buildBlob(r, ctx.accentMat, 1, 0.32);
    tuft.position.set(
      (Math.random() - 0.5) * 1.6 * s,
      r * 0.6,
      (Math.random() - 0.5) * 1.6 * s
    );
    group.add(tuft);
    if (ctx.emissiveMat) {
      const glow = buildBlob(r * 0.55, ctx.emissiveMat, 1, 0.3);
      glow.position.copy(tuft.position);
      glow.position.y += r * 0.4;
      group.add(glow);
    }
  }
  return group;
}

function buildStormFlower(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const stem = buildStem(0.35 * s, 0.04 * s, 0.025 * s, ctx.bodyMat, 0.4);
  group.add(stem);
  const ring = buildPetalRing(6, 0.28 * s, 0.13 * s, ctx.accentMat, 0.85);
  ring.position.y = 0.34 * s;
  group.add(ring);
  return group;
}

function buildIceFrond(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const fronds = 5 + Math.floor(Math.random() * 3);
  for (let i = 0; i < fronds; i++) {
    const length = (0.85 + Math.random() * 0.5) * s;
    const width = (0.14 + Math.random() * 0.06) * s;
    const frond = buildFrond(
      length,
      width,
      i % 2 === 0 ? ctx.bodyMat : ctx.accentMat,
      0.45
    );
    frond.rotation.set(
      0.55 + Math.random() * 0.25,
      (i / fronds) * Math.PI * 2 + Math.random() * 0.3,
      (Math.random() - 0.5) * 0.18
    );
    group.add(frond);
  }
  return group;
}

function buildMetalCrust(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const mat = buildMat(0.95 * s, ctx.bodyMat);
  group.add(mat);
  // Small mineral nodules scattered across the crust.
  const nodules = 3 + Math.floor(Math.random() * 3);
  for (let i = 0; i < nodules; i++) {
    const r = (0.08 + Math.random() * 0.08) * s;
    const nod = buildBlob(r, ctx.accentMat, 1, 0.35);
    nod.position.set(
      (Math.random() - 0.5) * 1.2 * s,
      r * 0.55,
      (Math.random() - 0.5) * 1.2 * s
    );
    nod.scale.y = 0.55 + Math.random() * 0.4;
    group.add(nod);
  }
  return group;
}

function buildPhotophoreBush(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  // A loose cluster of small glowing bulbs rising from a low body.
  const body = buildBlob(0.45 * s, ctx.bodyMat, 2, 0.2);
  body.position.y = 0.32 * s;
  body.scale.y = 0.7;
  group.add(body);

  const lights = 5 + Math.floor(Math.random() * 5);
  for (let i = 0; i < lights; i++) {
    const r = (0.1 + Math.random() * 0.08) * s;
    const stalk = buildStem(
      (0.3 + Math.random() * 0.2) * s,
      0.025 * s,
      0.018 * s,
      ctx.bodyMat,
      0.35
    );
    stalk.position.set(
      (Math.random() - 0.5) * 0.5 * s,
      0.32 * s,
      (Math.random() - 0.5) * 0.5 * s
    );
    group.add(stalk);
    const bulb = buildBlob(r, ctx.emissiveMat ?? ctx.accentMat, 1, 0.32);
    bulb.position.copy(stalk.position);
    bulb.position.y += (0.3 + Math.random() * 0.2) * s;
    group.add(bulb);
  }
  return group;
}

function buildLumenMat(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  const mat = buildMat(1.6 * s, ctx.bodyMat);
  group.add(mat);
  if (ctx.emissiveMat) {
    const sparkles = 8 + Math.floor(Math.random() * 6);
    for (let i = 0; i < sparkles; i++) {
      const r = (0.08 + Math.random() * 0.08) * s;
      const dot = buildBlob(r, ctx.emissiveMat, 1, 0.3);
      dot.position.set(
        (Math.random() - 0.5) * 1.8 * s,
        r * 0.4,
        (Math.random() - 0.5) * 1.8 * s
      );
      dot.scale.y = 0.4 + Math.random() * 0.3;
      group.add(dot);
    }
  }
  return group;
}

function buildCrackFungus(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  // Low irregular cluster of small bulbs rising from a flat patch — the
  // accent material reads as the cracked luminescent core.
  const mat = buildMat(0.85 * s, ctx.bodyMat);
  group.add(mat);

  const bulbs = 3 + Math.floor(Math.random() * 3);
  for (let i = 0; i < bulbs; i++) {
    const r = (0.18 + Math.random() * 0.12) * s;
    const bulb = buildBulb(r, ctx.accentMat);
    bulb.position.set(
      (Math.random() - 0.5) * 0.8 * s,
      0,
      (Math.random() - 0.5) * 0.8 * s
    );
    bulb.scale.y = 0.6 + Math.random() * 0.3;
    group.add(bulb);
    if (ctx.emissiveMat) {
      const core = buildBlob(r * 0.45, ctx.emissiveMat, 1, 0.3);
      core.position.copy(bulb.position);
      core.position.y += r * 0.55;
      group.add(core);
    }
  }
  return group;
}

function buildPulsingRoot(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  // Several thick curved roots emerging from a low base — they bend
  // outward and upward, taper to glowing tips. Reads as "raízes pulsantes
  // expostas" from Verdia.
  const rootCount = 4 + Math.floor(Math.random() * 3);
  for (let i = 0; i < rootCount; i++) {
    const angle = (i / rootCount) * Math.PI * 2 + Math.random() * 0.4;
    const length = (0.65 + Math.random() * 0.4) * s;
    const stem = buildStem(length, 0.13 * s, 0.05 * s, ctx.bodyMat, 0.5);
    // Tilt the root outward so they spread from the centre.
    stem.rotation.set(
      Math.PI * 0.42 + (Math.random() - 0.5) * 0.18,
      angle,
      0
    );
    stem.position.set(
      Math.cos(angle) * 0.05 * s,
      0,
      Math.sin(angle) * 0.05 * s
    );
    group.add(stem);

    // Glowing tip node — pulses with the spec emissive material.
    if (ctx.emissiveMat) {
      const tip = buildBlob(0.08 * s, ctx.emissiveMat, 1, 0.35);
      // Tip position approximates the curve's end at distance ~length
      // along the root's bent direction.
      const reach = length * 0.85;
      tip.position.set(
        Math.cos(angle) * reach * 0.7,
        length * 0.55,
        Math.sin(angle) * reach * 0.7
      );
      group.add(tip);
    }
  }
  // Low central nub holding the roots together.
  const nub = buildBlob(0.18 * s, ctx.accentMat, 1, 0.25);
  nub.position.y = 0.1 * s;
  nub.scale.y = 0.7;
  group.add(nub);
  return group;
}

function buildFibrousIceShrub(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  // Curved fibrous shrub: low rounded body wrapped in many thin curved
  // strands, like frost-glazed grass tufts. "Arbustos de gelo fibroso".
  const body = buildBlob(0.42 * s, ctx.bodyMat, 2, 0.22);
  body.position.y = 0.32 * s;
  body.scale.y = 0.7;
  group.add(body);

  const fibres = 8 + Math.floor(Math.random() * 5);
  for (let i = 0; i < fibres; i++) {
    const length = (0.55 + Math.random() * 0.35) * s;
    const fibre = buildFrond(length, 0.07 * s, ctx.accentMat, 0.6);
    fibre.rotation.set(
      0.2 + Math.random() * 0.4,
      (i / fibres) * Math.PI * 2 + Math.random() * 0.3,
      (Math.random() - 0.5) * 0.3
    );
    fibre.position.y = 0.42 * s;
    group.add(fibre);
  }

  if (ctx.emissiveMat) {
    const core = buildBlob(0.16 * s, ctx.emissiveMat, 1, 0.3);
    core.position.y = 0.42 * s;
    core.scale.y = 0.65;
    group.add(core);
  }
  return group;
}

function buildGlowStalk(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  // Tall slim curved stem with a glowing pod at the tip — "caules curvos
  // brilhantes" for Solenne Echo.
  const stalk = buildStem(1.05 * s, 0.05 * s, 0.025 * s, ctx.bodyMat, 0.32);
  group.add(stalk);

  if (ctx.emissiveMat) {
    const pod = buildBulb(0.18 * s, ctx.emissiveMat);
    pod.position.y = 1.0 * s;
    group.add(pod);
    const halo = buildBlob(0.12 * s, ctx.emissiveMat, 1, 0.3);
    halo.position.y = 1.05 * s;
    group.add(halo);
  } else {
    const pod = buildBulb(0.18 * s, ctx.accentMat);
    pod.position.y = 1.0 * s;
    group.add(pod);
  }
  return group;
}

function buildShadowMineral(s: number, ctx: BuildContext): THREE.Object3D {
  const group = new THREE.Group();
  // A small upright mineral colony — cluster of 3-5 elongated blobs.
  const cluster = 3 + Math.floor(Math.random() * 3);
  for (let i = 0; i < cluster; i++) {
    const r = (0.18 + Math.random() * 0.12) * s;
    const blob = buildBlob(r, i % 2 === 0 ? ctx.bodyMat : ctx.accentMat, 2, 0.35);
    blob.scale.y = 1.6 + Math.random() * 0.6;
    const angle = (i / cluster) * Math.PI * 2;
    const dist = 0.2 * s + Math.random() * 0.2 * s;
    blob.position.set(
      Math.cos(angle) * dist,
      r * 0.55,
      Math.sin(angle) * dist
    );
    blob.rotation.z = (Math.random() - 0.5) * 0.25;
    group.add(blob);
  }
  return group;
}
